import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import { getCountries } from 'Reducers/Countries/actions'
import { getCities } from 'Reducers/City/actions'
import { Col, Row } from 'react-bootstrap'
import { authorization } from 'Module/Auth/Auth'
import { Input } from './../../../../Components/Form/Input'

import { SectionTitle } from './../../../../Components/Section/SectionTitle'
import { SectionWrapper } from './../../../../Components/Section/SectionWrapper'
import { SelectDashboard } from './../../../../Components/Form/SelectDashboard'
import SubmitButton from './../../../../Components/Form/SubmitButton'
import Switch from './../../../../Components/Form/Switch'
import { Slider } from './../../../../Components/Form/Slider'
import { Checkbox } from './../../../../Components/Form/Checkbox'
import { getInitialOverlaysMapFromMissions } from '../../../LetterboxMissions/util.js'
import {
  STATUS_CAMPAIGN_ENUM_DRAFT,
  STATUS_CAMPAIGN_ENUM_ACTIVE,
  STATUS_CAMPAIGN_ENUM_ARCHIVE,
  STATUS_CAMPAIGN_ENUM_COMPLETED,
  CAMPAIGN_ENUM_TYPES,
} from '../../../../shared/constants'
import {
  showReduxModal,
  toggleConfirmAvailabilityReduxModal,
} from '../../../../Reducers/UI/actions'
import CampaignBuilderMap from '../../../Campaigns/CreateEditCampaign/CampaignBuilderMap/CampaignBuilderMap'
import LoadingIndicator from '../../../../Components/LoadingIndicator/LoadingIndicator'
import CampaignQuote from '../../../../Components/CampaignQuote/container/CampaignQuote'
import { getOverlayBoundary } from '../../../../Components/DrawMap/util'
import exportComponentAsPDF from '../../../../Utils/ExportComponentAsPDF'
import CampaignPrintingPlan from './CampaignPrintingPlan'
import MetricsEdit from './../../../../Components/MetricsEdit'
import {
  convertMonetaryDollarStringToCentValue,
  convertCentValueToMonetaryString,
} from './../../../../Utils/Formatters'
import {
  runValidation as runValidationShared,
  isRequired,
} from './../../../../shared/validation'
import { ErrorMessage } from './../../../../Components/Form/ErrorMessage'
import { isPathFinderSupportsCountry } from '../../../../shared/libraries/geo'

// Define the form validation
/**
 * Run validation over a function, can pass it a dto and it will return any
 * error messages if the field isn't valid else will return null
 */
var runValidation = runValidationShared.bind(null, {
  clientId: isRequired,
  countryId: isRequired,
  name: isRequired,
  printingCostPerFlyer: isRequired,
})

const isClient = () => authorization.isClient() || authorization.isClientAdmin()

/**
 * @typedef Client
 * @property {string} name
 * @property {string} id
 *
 * @typedef Country
 * @property {string} name
 * @property {string} id
 *
 * @typedef CreateEditCampaignFormProps
 * @property {string} type One of either 'HANDTOHAND', 'LETTERBOX' or ;DIRECTMAIL. See
 * CAMPAIGN_ENUM_TYPES
 * @property {boolean} bonusEnabled
 * @property {string} status One of either 'DRAFT', 'ACTIVE', 'ARCHIVED'
 * @property {string} clientId The campaign client id, this is only changable
 * in create mode.
 * @property {Array<Client>} clients The selectable clients when creating a
 * campaign. If in edit mode ensure it at least contains the client for the
 * campaign. Only pass in active clients.
 * @property {string} countryId The campaign country id, this is only changable
 * in create mode.
 * @property {Array<Country>} countries The selectable countries when creating a
 * campaign. If in edit mode ensure it at least contains the country for the
 * campaign.
 * @property {string} name The campaign name
 * @property {number} printingCostPerFlyer The printing cost per flyer in cents.
 * @property {number} baRedemptionPeriod The BA redemption period in weeks.
 * @property {string} pitchUrl
 * @property {number} basePayout The base payout in cents.
 * @property {number} distributionCost The distribution cost in cents.
 * @property {number} payoutPerConversion The bonus payout per conversion in
 * cents
 * @property {number} bonusLimit The upper limit on bonuses that can be
 * paid out in cents.
 * @property {?} overlaysMap
 *
 * @param {CreateEditCampaignFormProps} props
 */
const CreateEditCampaignFormUnstyled = props => {
  const { editMode } = props

  // Form values, anything that gets updated will end up in here
  const [errors, setErrors] = useState({})
  const [values, setValues] = useState({})

  const setValue = (prop, value) =>
    setValues(values => ({ ...values, [prop]: value }))

  const initialValues = {
    ...props,
    printingCostPerFlyer: convertCentValueToMonetaryString(
      props.printingCostPerFlyer,
    ),
    basePayout: convertCentValueToMonetaryString(props.basePayout),
    distributionCost: convertCentValueToMonetaryString(props.distributionCost),
    payoutPerConversion: convertCentValueToMonetaryString(
      props.payoutPerConversion,
    ),
    bonusLimit: convertCentValueToMonetaryString(props.bonusLimit),
  }

  const {
    type,
    bonusEnabled,
    status,
    clientId,
    countryId,
    name,
    printingCostPerFlyer,
    baRedemptionPeriod,
    pitchUrl,
    basePayout,
    distributionCost,
    payoutPerConversion,
    bonusLimit,
    overlaysMap,
    prebuiltOverlays,
  } = { ...initialValues, ...values }
  useEffect(() => {
    props.getCountries()
    props.getCities()
  }, [])

  const isH2HType = type == CAMPAIGN_ENUM_TYPES.HANDTOHAND
  const isLBType = type == CAMPAIGN_ENUM_TYPES.LETTERBOX
  const isDMType = type == CAMPAIGN_ENUM_TYPES.DIRECTMAIL

  const bonusDisabled = editMode && props.status !== STATUS_CAMPAIGN_ENUM_DRAFT

  const renderClientSelect = () => {
    if (props.editMode) {
      const client = props.clients.find(client => client.id == clientId)
      return (
        <div className="disabled-field">
          <label>Client</label>
          <span>{client ? client.name : '-'}</span>
        </div>
      )
    }
    return (
      <SelectDashboard
        options={props.clients.map(dto => ({ label: dto.name, value: dto.id }))}
        value={clientId}
        onChange={(a, b, value) => setValue('clientId', value)}
        label="Client*"
        sorted={true}
        error={errors['clientId']}
      />
    )
  }

  const renderCountrySelect = () => {
    if (props.editMode) {
      const country = props.countries.find(country => country.id == countryId)
      return (
        <div className="disabled-field">
          <label>Country</label>
          <span>{country ? country.name : '-'}</span>
        </div>
      )
    }

    return (
      <SelectDashboard
        options={props.countries.map(dto => ({
          label: dto.name,
          value: String(dto.id),
        }))}
        value={String(countryId)}
        onChange={(a, b, value) => setValue('countryId', value)}
        label="Country*"
        sorted={true}
        error={errors['countryId']}
      />
    )
  }

  const renderStatusSelect = () => {
    const { editMode } = props
    if (!editMode) return null

    const selectOptions = [
      {
        label: 'Active',
        value: STATUS_CAMPAIGN_ENUM_ACTIVE,
      },
      {
        label: 'Archived',
        value: STATUS_CAMPAIGN_ENUM_ARCHIVE,
      },
      {
        label: 'Completed',
        value: STATUS_CAMPAIGN_ENUM_COMPLETED,
      },
    ]

    if (status === STATUS_CAMPAIGN_ENUM_DRAFT) {
      selectOptions.push({
        label: 'Draft',
        value: STATUS_CAMPAIGN_ENUM_DRAFT,
      })
    }

    return (
      <SelectDashboard
        onChange={(a, b, value) => setValue('status', value)}
        options={selectOptions}
        label="Status*"
        value={status}
        error={errors['status']}
      />
    )
  }
  const getCurrencyInputClass = () => {
    let currencySign
    if (countryId) {
      let country = (props.countries || []).find(({ id }) => id === countryId)
      if (country) {
        currencySign = country.currency
      }
    }

    switch (currencySign) {
      case '$':
        return 'input-with-icon_currency_dollar'
      case '€':
        return 'input-with-icon_currency_euro'
      case '£':
        return 'input-with-icon_currency_pound'
      case 'CHF':
        return 'input-with-icon_currency_chf'
      default:
        return ''
    }
  }

  const onSubmit = e => {
    const { showReduxModal } = props

    e.stopPropagation()
    e.preventDefault()

    var lErrors = runValidation({ ...initialValues, ...values })
    if (Object.values(lErrors).filter(v => v).length) return setErrors(lErrors)

    var handleSubmit = () => {
      var values = {
        type,
        bonusEnabled,
        status,
        clientId,
        countryId,
        name,
        printingCostPerFlyer: convertMonetaryDollarStringToCentValue(
          printingCostPerFlyer,
        ),
        baRedemptionPeriod: Number(baRedemptionPeriod),
        pitchUrl,
        basePayout: basePayout,
        distributionCost,
        payoutPerConversion,
        bonusLimit: bonusLimit,
        overlaysMap,
      }
      return props.onSubmit(values)
    }

    if (
      props.status == STATUS_CAMPAIGN_ENUM_DRAFT &&
      status == STATUS_CAMPAIGN_ENUM_ACTIVE
    ) {
      return showReduxModal({
        title: 'Activate Campaign',
        onConfirm: handleSubmit,
        children: confirmationModalBody(),
        confirmDisabled: true,
        confirmLabel: 'Activate campaign',
      })
    }

    return handleSubmit()
  }

  const confirmationModalBody = () => {
    const {
      toggleConfirmAvailabilityReduxModal,
      countries,
      metrics,
      primaryMetricIndex,
    } = props

    let metricNamesStr = 'NOT SET'
    let primaryMetricName = 'NOT SET'
    let payoutPerConversionStr = ''
    let bonusLimitStr = ''
    let currency = ''
    if (metrics) {
      const metricNamesArr = metrics.map((metric, index) => {
        if (index === primaryMetricIndex) {
          primaryMetricName = metric.name
        }
        return metric.name
      })
      metricNamesStr = metricNamesArr.join(', ')
    }

    if (countryId) {
      const country = countries.find(({ id }) => id === countryId)
      if (country) {
        currency = country.currency
      }
    }

    if (payoutPerConversion) {
      bonusLimitStr = `${currency}${bonusLimit}`
      payoutPerConversionStr = `${currency}${payoutPerConversion}`
    }

    return (
      <section className="redux-modal">
        <article>
          <p>
            This data (and more) will be set and UN-EDITABLE for this campaign:
          </p>
          <ul className="redux-modal__list">
            <li>Primary Metric: "{primaryMetricName}"</li>
            <li>Metric Names: "{metricNamesStr}"</li>
            <li>
              Conversion Bonus:
              {bonusEnabled ? (
                <ul>
                  <li>ENABLED</li>
                  <li>Redemption Period: {baRedemptionPeriod} weeks</li>
                  <li>Bonus Allowance: {bonusLimitStr}</li>
                  <li>Bonus Per Conversion: {payoutPerConversionStr}</li>
                </ul>
              ) : (
                <ul>
                  <li>DISABLED</li>
                </ul>
              )}
            </li>
            <li>Printing Cost Per Flyer: {printingCostPerFlyer}</li>
          </ul>
        </article>
        <footer className="redux-modal__footer">
          <Checkbox
            input={{
              onClick: toggleConfirmAvailabilityReduxModal,
            }}
          />
          <span className="redux-modal__checkbox-hint">
            I understand these CANNOT BE EDITED after activating this campaign.
          </span>
        </footer>
      </section>
    )
  }

  const getCountry = () =>
    (props.countries || []).find(country => country.id == countryId) || {}

  const country = useMemo(getCountry, [countryId, props.countries])

  const existingBoundaries = useMemo(() => {
    if (isH2HType || !editMode) return []
    if (!props.missions || !props.missions.length) return []

    const allOverlays = Object.values(
      getInitialOverlaysMapFromMissions(props.missions, {
        isClient: isClient(),
        clickable: false,
      }),
    )
      .map(node => ({
        ...node,
        color: '#bdbdbd',
        boundary: node.paths[0].map(item => ({
          longitude: item.lng,
          latitude: item.lat,
        })),
      }))
      .filter(node => node.paths[0][0].lng)

    return allOverlays
  }, [
    isH2HType,
    editMode,
    props.campaign && props.campaign.cityPlans,
    props.missions,
  ])

  const getOverlaysForQuote = () => {
    if (isH2HType) return null
    const newOverlays = Object.values(overlaysMap || {}).map(overlay => ({
      id: overlay.customId,
      boundary: getOverlayBoundary(overlay),
    }))
    return [...(existingBoundaries || []), ...newOverlays]
  }

  const campaignsOverlays = useMemo(getOverlaysForQuote, [
    existingBoundaries,
    overlaysMap,
  ])

  const getCampaignCountryCities = () =>
    props.cities.filter(city => city.country.code === country.code)

  const campaignCountryCities = useMemo(getCampaignCountryCities, [
    country.code,
    props.cities,
  ])

  // eslint-disable-next-line
  const [isSummaryLoading, setIsSummaryLoading] = useState(false)

  const [isPrintingPlanLoading, setIsPrintingPlanLoading] = useState(false)

  const onExportPrintingPlanClick = async () => {
    setIsPrintingPlanLoading(true)

    await exportComponentAsPDF({
      renderContent: ({ onInit }) => (
        <CampaignPrintingPlan>
          <CampaignQuote
            overlays={campaignsOverlays}
            metrics={[]}
            printingCostPerFlyer={
              typeof printingCostPerFlyer == 'string'
                ? convertMonetaryDollarStringToCentValue(printingCostPerFlyer)
                : printingCostPerFlyer
            }
            distributionCost={
              typeof distributionCost == 'string'
                ? convertMonetaryDollarStringToCentValue(distributionCost)
                : distributionCost
            }
            bonusEnabled={bonusEnabled}
            bonusLimit={
              typeof bonusLimit == 'string'
                ? convertMonetaryDollarStringToCentValue(bonusLimit)
                : bonusLimit
            }
            payoutPerConversion={
              typeof payoutPerConversion == 'string'
                ? convertMonetaryDollarStringToCentValue(payoutPerConversion)
                : payoutPerConversion
            }
            cities={campaignCountryCities}
            country={country}
            hideColumns={[
              'Density',
              '# Missions',
              'Total Dist Cost',
              'Total Printing Cost',
              'Bonus Cost',
              'Total',
            ]}
            simpleView
            onDataLoaded={onInit} //Initialize printing when table is loaded
          />
        </CampaignPrintingPlan>
      ),
      delayedRender: true,
      title: 'Campaign printing plan',
    })
    setIsPrintingPlanLoading(false)
  }

  return (
    <form className={props.className}>
      <SectionWrapper className="section-wrapper">
        <SectionTitle className="section-title">Campaign Type</SectionTitle>
        <div className="type-buttons">
          <ButtonGroup disableElevation color="primary" disabled={editMode}>
            <SubmitButton
              value="Hand to hand"
              variant={isH2HType ? 'contained' : 'outlined'}
              onClick={() => setValue('type', CAMPAIGN_ENUM_TYPES.HANDTOHAND)}
            />
            <SubmitButton
              value="Letterbox"
              variant={isLBType ? 'contained' : 'outlined'}
              onClick={() => setValue('type', CAMPAIGN_ENUM_TYPES.LETTERBOX)}
            />
            <SubmitButton
              value="Direct Mail"
              variant={isDMType ? 'contained' : 'outlined'}
              onClick={() => setValue('type', CAMPAIGN_ENUM_TYPES.DIRECTMAIL)}
            />
          </ButtonGroup>
          <ErrorMessage>{errors['type']}</ErrorMessage>
        </div>
      </SectionWrapper>
      <SectionWrapper className="section-wrapper">
        <Row>
          <SectionTitle className="section-title-inline">
            Campaign Details
          </SectionTitle>
          {isLBType && isPathFinderSupportsCountry(country.code) ? (
            <React.Fragment>
              <div className="printing-plan-btns">
                <SubmitButton
                  className={isPrintingPlanLoading ? 'transparent' : ''}
                  type="Button"
                  value="Export Printing plan"
                  onClick={onExportPrintingPlanClick}
                  disabled={isPrintingPlanLoading}
                />
              </div>
            </React.Fragment>
          ) : null}
        </Row>
        <Row>
          <Col xs={6}>{renderClientSelect()}</Col>
          <Col xs={6}>{renderCountrySelect()}</Col>
        </Row>
        <Row>
          <Col xs={6}>
            <Input
              type="text"
              label="Name*"
              value={name}
              onChange={e => setValue('name', e.target.value)}
              error={errors['name']}
            />
          </Col>
          <Col xs={6}>
            <Input
              className={`input-with-icon  ${getCurrencyInputClass()}`}
              type="number"
              label="Printing cost per flyer*"
              value={printingCostPerFlyer}
              onChange={e => setValue('printingCostPerFlyer', e.target.value)}
              error={errors['printingCostPerFlyer']}
            />
          </Col>
        </Row>
        <Row>
          {editMode && <Col xs={6}>{renderStatusSelect()}</Col>}

          <Col xs={6}>
            <Slider
              unit="weeks"
              label="Redemption Time"
              min={2}
              max={6}
              defaultValue={baRedemptionPeriod}
              value={baRedemptionPeriod}
              onChange={value => setValue('baRedemptionPeriod', value)}
              error={errors['baRedemptionPeriod']}
            />
          </Col>
        </Row>
      </SectionWrapper>
      <SectionWrapper
        className={`section-wrapper ${bonusDisabled ? 'disabled' : ''}`}
      >
        <div className="bonus-title-container">
          <SectionTitle className="section-title">
            Conversion Bonus
          </SectionTitle>
          {!bonusDisabled && (
            <Switch
              value={bonusEnabled}
              input={{
                onChange: e => setValue('bonusEnabled', e.target.checked),
              }}
            />
          )}
        </div>
        <div className="bonus-container">
          <div className="left-box">
            <Input
              disabled={!bonusEnabled || bonusDisabled}
              className={`input-with-icon ${getCurrencyInputClass()}`}
              placeholder="Bonus allowance"
              type="number"
              label="Bonus Allowance*"
              value={bonusLimit}
              onChange={e => setValue('bonusLimit', e.target.value)}
              error={errors['bonusLimit']}
            />
          </div>
          <div className="right-box">
            <Input
              disabled={!bonusEnabled || bonusDisabled}
              className={`input-with-icon ${getCurrencyInputClass()}`}
              placeholder="Bonus Per Conversion"
              type="number"
              label="Bonus Per Conversion*"
              value={payoutPerConversion}
              onChange={e => setValue('payoutPerConversion', e.target.value)}
              error={errors['payoutPerConversion']}
            />
          </div>
        </div>
      </SectionWrapper>

      <SectionWrapper className="section-wrapper metrics">
        <SectionTitle className="section-title">Campaign Metrics</SectionTitle>
        <MetricsEdit
          metrics={props.metrics}
          onMetricUpdate={props.onMetricUpdate}
          onMetricDelete={props.onMetricDelete}
          onMetricAdd={props.onMetricAdd}
          primaryIndex={props.primaryMetricIndex}
          onPrimaryIndexChange={props.onPrimaryMetricIndexChange}
        />
      </SectionWrapper>
      {isLBType && isPathFinderSupportsCountry(country.code) && (
        <React.Fragment>
          <CampaignQuote
            overlays={campaignsOverlays}
            metrics={props.metrics}
            printingCostPerFlyer={
              typeof printingCostPerFlyer == 'string'
                ? convertMonetaryDollarStringToCentValue(printingCostPerFlyer)
                : printingCostPerFlyer
            }
            distributionCost={
              typeof distributionCost == 'string'
                ? convertMonetaryDollarStringToCentValue(distributionCost)
                : distributionCost
            }
            bonusEnabled={bonusEnabled}
            bonusLimit={
              typeof bonusLimit == 'string'
                ? convertMonetaryDollarStringToCentValue(bonusLimit)
                : bonusLimit
            }
            payoutPerConversion={
              typeof payoutPerConversion == 'string'
                ? convertMonetaryDollarStringToCentValue(payoutPerConversion)
                : payoutPerConversion
            }
            cities={campaignCountryCities}
            country={country}
          />
          <SectionWrapper className="section-wrapper">
            <SectionTitle>Campaign Map</SectionTitle>
            {country.code ? (
              <CampaignBuilderMap
                countryCode={country.code}
                onUpdate={value => setValue('overlaysMap', value)}
                existingBoundaries={existingBoundaries}
                cities={props.cities}
                prebuiltOverlays={prebuiltOverlays}
              />
            ) : (
              'Please, select a country first'
            )}
          </SectionWrapper>
        </React.Fragment>
      )}
      <footer
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          float: 'right',
          width: '100%',
        }}
      >
        {props.loading && (
          <LoadingIndicator
            style={{
              flexDirection: 'row',
              width: 'unset',
              height: 'unset',
              marginRight: '24px',
            }}
            labelStyle={{ marginTop: '0', marginLeft: '8px' }}
            label={props.loadingLabel}
            size={35}
          />
        )}
        <SubmitButton
          value={editMode ? 'Save campaign' : 'Create campaign'}
          id="CreateClient-submitButton"
          disabled={props.loading}
          onClick={onSubmit}
        />
      </footer>
    </form>
  )
}

CreateEditCampaignFormUnstyled.propTypes = {
  clients: PropTypes.array.isRequired,
  canDeletePrimaryMetric: PropTypes.bool,
}

const mapStateToProps = (state, props) => ({
  countries: state.countries.countriesList,
})

const mapDispatchToProps = {
  showReduxModal,
  toggleConfirmAvailabilityReduxModal,
  getCountries,
  getCities,
}

const CreateEditCampaignFormUnstyledFormConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateEditCampaignFormUnstyled)

export default CreateEditCampaignFormUnstyledFormConnected



// WEBPACK FOOTER //
// ./src/Containers/Form/Campaigns/CreateEditCampaign/unstyled.js